import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Message, Header, Dropdown, Button, Icon, Loader, Segment } from 'semantic-ui-react';
import apiClient from '../../apiTools/apiClient';

/**
 * ReaderNotes — the quality-control table.
 *
 * ⭐ WHAT THIS REPLACES is the point of it. Six readers have the books for quality control, and
 * without somewhere to put a finding it arrives as the operator described: *"email and texts or
 * pictures."* Three columns were the whole specification — **Book, where, what** — and the reading
 * end is this panel.
 *
 * ⛔ `where` AND `what` ARE FREE TEXT. The operator's call: *"It can be free form data."* A reader
 * has a chapter title and a running clock in the Files app, and will write that however they write
 * it. Nothing here parses either field, and nothing should start: the moment this table tries to
 * sort by position it needs a format the readers were never asked to use.
 *
 * ⓘ TWO COLUMNS THE SPEC DID NOT NAME, and both earn it once six people are writing: **Reader**,
 * because an unattributed finding cannot be followed up, and **When**, because the same chapter gets
 * re-rendered and a note older than the take it describes may be about audio that no longer exists.
 *
 * ⚠️ READ-ONLY, DELIBERATELY. There is no status, no triage, no reply — those are the *someday*
 * feature the operator parked: *"Someday maybe enhanced into a real QA feature, allowed by specific
 * people."* Adding a status column now would mean deciding the workflow before the first note has
 * been read.
 */

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  : '—');

/**
 * ⓘ Plain text, not CSV. These notes go into a message or a working list, not a spreadsheet, and a
 * free-text field full of commas and newlines is exactly what CSV handles worst.
 */
const asText = (rows) => rows.map((n) => [
  (n.bookTitle || 'Unknown book') + ' — ' + (n.readerName || n.readerEmail || 'unknown reader'),
  'where: ' + (n.whereText || '(not given)'),
  'what:  ' + (n.whatText || ''),
].join('\n')).join('\n\n');

export default function ReaderNotes() {
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);
  const [book, setBook] = useState('all');
  const [reader, setReader] = useState('all');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await apiClient.get('/reader-notes');
      setNotes(data.notes || []);
    } catch (e) {
      setNotes([]);
      setError('Could not load reader notes.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bookOptions = useMemo(() => {
    const seen = new Map();
    (notes || []).forEach((n) => {
      const key = n.bookId || n.bookTitle || 'unknown';
      if (!seen.has(key)) seen.set(key, n.bookTitle || 'Unknown book');
    });
    return [{ key: 'all', value: 'all', text: 'All books' }]
      .concat([...seen.entries()].map(([value, text]) => ({ key: value, value, text })));
  }, [notes]);

  const readerOptions = useMemo(() => {
    const seen = new Set();
    (notes || []).forEach((n) => {
      const who = n.readerName || n.readerEmail;
      if (who) seen.add(who);
    });
    return [{ key: 'all', value: 'all', text: 'All readers' }]
      .concat([...seen].sort().map((who) => ({ key: who, value: who, text: who })));
  }, [notes]);

  const shown = useMemo(() => (notes || []).filter((n) => {
    const bookKey = n.bookId || n.bookTitle || 'unknown';
    const who = n.readerName || n.readerEmail || '';
    return (book === 'all' || bookKey === book) && (reader === 'all' || who === reader);
  }), [notes, book, reader]);

  /**
   * ⓘ The clipboard write can be refused (an insecure origin, or a browser that withholds it), so
   * the confirmation only appears when it resolved. A silent failure that still said "Copied" would
   * be worse than no button.
   */
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText(shown));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      setError('This browser would not let the page write to the clipboard.');
    }
  };

  return (
    <Segment basic style={{ padding: '1rem 1.5rem' }}>
      <Header as="h2">
        Reader notes
        <Header.Subheader>
          What the quality-control readers found. Free-form, newest first.
        </Header.Subheader>
      </Header>

      {error && <Message negative content={error} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 0 1rem', flexWrap: 'wrap' }}>
        <Dropdown selection options={bookOptions} value={book}
          onChange={(e, d) => setBook(d.value)} style={{ minWidth: '14rem' }} />
        <Dropdown selection options={readerOptions} value={reader}
          onChange={(e, d) => setReader(d.value)} style={{ minWidth: '12rem' }} />
        <Button basic icon labelPosition="left" onClick={load}>
          <Icon name="refresh" />Refresh
        </Button>
        <Button basic icon labelPosition="left" onClick={copy} disabled={!shown.length}>
          <Icon name={copied ? 'check' : 'copy'} />{copied ? 'Copied' : 'Copy as text'}
        </Button>
        <span style={{ color: 'rgba(0,0,0,0.5)' }}>
          {notes === null ? '' : shown.length + ' of ' + notes.length}
        </span>
      </div>

      {notes === null && <Loader active inline="centered" content="Loading…" />}

      {notes !== null && !notes.length && (
        <Message info
          header="No notes yet"
          content="Readers add these from their downloads page, one per finding." />
      )}

      {!!shown.length && (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={3}>Book</Table.HeaderCell>
              <Table.HeaderCell width={3}>Where</Table.HeaderCell>
              <Table.HeaderCell width={7}>What</Table.HeaderCell>
              <Table.HeaderCell width={3}>Reader</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {shown.map((n) => (
              <Table.Row key={n.uuid} verticalAlign="top">
                <Table.Cell>
                  {n.bookTitle || 'Unknown book'}
                  {n.itemTitle && <div style={{ color: 'rgba(0,0,0,0.5)' }}>{n.itemTitle}</div>}
                </Table.Cell>
                <Table.Cell>{n.whereText || <span style={{ color: 'rgba(0,0,0,0.4)' }}>—</span>}</Table.Cell>
                {/* ⚠️ `pre-wrap`: a reader's line breaks are part of what they wrote. */}
                <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>{n.whatText}</Table.Cell>
                <Table.Cell>
                  {n.readerName || n.readerEmail || 'unknown'}
                  <div style={{ color: 'rgba(0,0,0,0.5)' }}>{fmtDate(n.createdAt)}</div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
}
